import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Divider } from "@material-ui/core";
import { savePaymentMethod } from "../redux/retailerWholesalerCartSlice";
import { PAYMENT_METHODS } from "../config/market";
import paymentBackgroundStyle from "./paymentBackground";
import RetailerWholesalerNavbar from "../components/RetailerWholesalerNavbar";
import CheckoutStepper from "../components/CheckoutStepper";
import styled from "styled-components";
import { resolveProductPaymentPolicy } from "../utils/productPaymentPolicy";
import useCodPolicy from "../hooks/useCodPolicy";

const PaymentOptionButton = styled.button`
  border: none;
  padding: 15px 20px;
  background-color: teal;
  color: white;
  cursor: pointer;
  margin-top: 10px;
  border-radius: 4px;
  &:disabled {
    background-color: #ccc;
    color: #aaa;
    font-weight: 300;
    font-size: 14px;
    cursor: not-allowed;
  }
`;

const WholesalerRetailerPaymentScreen = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const currentVendor = useSelector((state) => state.retailerWholesalerCart.currentVendor);
    const cart = useSelector((state) => state.retailerWholesalerCart.carts[currentVendor] || {});
    const products = Object.values(cart);
    const [selectedOption, setSelectedOption] = useState(-1);

    useEffect(() => {
        if (!currentVendor) {
            history.push('/');
        } else if (products.length === 0) {
            history.push("/cart"); // old target was an unrouted path that fell through to "/"
        }
    }, [history, products, currentVendor]);

    const submitHandler = (e) => {
        e.preventDefault();
        if (selectedOption === -1) return;
        dispatch(savePaymentMethod(paymentOptions[selectedOption].value));
        history.push(`/retailer-wholesaler-placeorder/${currentVendor}`);
    };

    const handleOptionChange = (index) => {
        setSelectedOption(index);
    };

    // ACTIVE market registry (config/market.js) mapped onto this screen's
    // `value` shape. UG renders its original four; ZM adds Zamtel Kwacha.
    const allPaymentOptions = PAYMENT_METHODS.map((m) => ({
        label: m.label,
        value: m.key,
        imageUrl: m.imageUrl,
    }));

    /* The live platform COD ceiling. Until it has loaded this is null and
       the policy below leaves Cash on delivery exactly where it was — see
       resolveProductPaymentPolicy for why a shipped constant would be worse
       than no ceiling at all. */
    const { maxProductAmount: codMaxProductAmount } = useCodPolicy();
    const paymentPolicy = resolveProductPaymentPolicy(products, allPaymentOptions, {
      codMaxProductAmount,
    });
    const paymentOptions = paymentPolicy.options;

    useEffect(() => {
        if (selectedOption >= paymentOptions.length) setSelectedOption(-1);
        if (selectedOption === -1 && paymentOptions.length === 1) setSelectedOption(0);
    }, [paymentOptions.length, selectedOption]);

    const optionStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "12px 16px",
        borderRadius: "4px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#DBF3FA" : "#EDF2F7",
        boxShadow: isSelected ? "0px 2px 5px rgba(0, 0, 0, 0.1)" : "none",
        transition: "all 0.3s",
        textAlign: "center",
        fontWeight: isSelected ? "bold" : "normal",
        marginBottom: "16px",
        height: "60px",
    });

    return (
        <>
            <RetailerWholesalerNavbar />
            <div
                style={{
                    width: "100vw",
                    height: "100vh",
                    ...paymentBackgroundStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "30px",
                }}
            >
                <div
                    style={{
                        maxWidth: "500px",
                        width: "80%",
                        padding: "20px",
                        backgroundColor: "white",
                        borderRadius: "10px",
                        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.9)",
                    }}
                >
                    <CheckoutStepper currentStep="payment" />
                    <h1
                        style={{ fontSize: "24px", fontWeight: "300", textAlign: "center" }}
                    >
                        PAYMENT OPTIONS
                    </h1>
                    <Divider />
                    <form>
                        <label
                            style={{
                                display: "block",
                                marginBottom: "10px",
                                marginTop: "20px",
                                fontSize: "16px",
                                fontStyle: "italic",
                                color: "#4A4A4A",
                            }}
                        >
                            Select Payment Method
                        </label>
                        {paymentPolicy.notice && (
                            <div style={{
                                backgroundColor: "#fff8e1",
                                border: "1px solid #f59e0b",
                                borderRadius: "6px",
                                padding: "8px 12px",
                                marginBottom: "12px",
                                fontSize: "13px",
                                color: "#92400e",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}>
                                <span>⚠️</span>
                                <span>{paymentPolicy.notice}</span>
                            </div>
                        )}
                        {paymentOptions.map(({ label, imageUrl }, index) => (
                            <div
                                key={index}
                                style={optionStyle(selectedOption === index)}
                                onClick={() => handleOptionChange(index)}
                            >
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={label}
                                        style={{ width: "50px", height: "44px", marginRight: "5px" }}
                                    />
                                ) : (
                                    <div style={{ width: "50px", height: "44px", marginRight: "5px" }} />
                                )}
                                <div
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        borderRadius: "50%",
                                        border:
                                            selectedOption === index
                                                ? "3px solid #3f83f8"
                                                : "1px solid #D1D5DB",
                                        backgroundColor:
                                            selectedOption === index ? "#3f83f8" : "transparent",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "8px",
                                    }}
                                >
                                    {selectedOption === index && (
                                        <div
                                            style={{
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: "white",
                                            }}
                                        ></div>
                                    )}
                                </div>
                                {label}
                            </div>
                        ))}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <PaymentOptionButton
                                variant="contained"
                                color="primary"
                                disabled={selectedOption === -1}
                                style={{
                                    width: "150px",
                                    marginTop: "10px",
                                }}
                                onClick={submitHandler}
                            >
                                Continue
                            </PaymentOptionButton>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default WholesalerRetailerPaymentScreen;
